import { useState, useEffect, useRef } from "react"
import { Box } from "@mui/material"
import { footerToHtml } from "@crm-src/components/webinar-builder/footer-section"
import {
  BrochureSection,
  BrochureFeature,
  TenantInfo,
  showcaseImageOptions,
} from "@crm-src/components/brochure-builder/stock-images"
import { EMAIL_DOCUMENT_OPEN, EMAIL_DOCUMENT_CLOSE } from "@crm-src/components/brochure-builder/email-template"
import { HeroSection, heroToHtml } from "@crm-src/components/brochure-builder/hero-section"
import { HighlightsSection, highlightsToHtml } from "@crm-src/components/brochure-builder/highlights-section"
import { ShowcaseSection, showcaseToHtml } from "@crm-src/components/brochure-builder/showcase-section"
import { CtaSection, ctaToHtml } from "@crm-src/components/brochure-builder/cta-section"

interface BrochureBuilderProps {
  tenantName: string
  tenantInfo?: TenantInfo
  initialSections?: BrochureSection[]
  onContentChange: (html: string) => void
  onSectionsChange?: (sections: BrochureSection[]) => void
}

// A modern, illustration-forward "brochure" email builder. Emits a COMPLETE
// HTML document (crm-api renders it verbatim, like the webinar/newsletter
// layouts) built from table-based, inline-styled sections that hold up across
// every mail client — emoji "illustrations", bulletproof VML buttons, and
// Outlook width/height image attributes. See ./brochure-builder/html-helpers.ts.
export function BrochureBuilder({
  tenantName,
  tenantInfo,
  initialSections,
  onContentChange,
  onSectionsChange,
}: BrochureBuilderProps) {
  const getDefaultSections = (): BrochureSection[] => [
    {
      id: "hero",
      type: "hero",
      content: {
        eyebrow: "Introducing",
        headline: "A smarter way to stay connected",
        subheadline: "Everything your team needs to communicate, collaborate, and grow — in one modern platform.",
        ctaLabel: "Learn more",
        ctaUrl: "",
      },
    },
    {
      id: "highlights",
      type: "highlights",
      content: {
        heading: "Why teams choose us",
        features: [
          { id: "feature-1", emoji: "🚀", title: "Fast to launch", text: "Get up and running in minutes, not months." },
          { id: "feature-2", emoji: "🔒", title: "Secure by design", text: "Enterprise-grade protection on every message." },
          { id: "feature-3", emoji: "🤝", title: "Always supported", text: "A dedicated team ready whenever you need us." },
        ] as BrochureFeature[],
      },
    },
    {
      id: "showcase",
      type: "showcase",
      content: {
        imageUrl: showcaseImageOptions[0]?.url ?? "",
        heading: "Built for how you work",
        body: "A clean, intuitive experience your whole team will actually enjoy using — on any device, anywhere.",
        ctaLabel: "See how it works",
        ctaUrl: "",
      },
    },
    {
      id: "cta",
      type: "cta",
      content: {
        heading: "Ready to get started?",
        subtext: "Talk to our team and see what we can do for you.",
        ctaLabel: "Get in touch",
        ctaUrl: "",
      },
    },
  ]

  // Merge saved sections over the defaults so a re-opened campaign keeps its
  // content while any newly-added section type still appears.
  const ensureAllSections = (saved: BrochureSection[] | undefined): BrochureSection[] => {
    if (!saved || saved.length === 0) return getDefaultSections()
    const defaults = getDefaultSections()
    return defaults.map((def) => saved.find((s) => s.type === def.type) ?? def)
  }

  const [sections, setSections] = useState<BrochureSection[]>(() => ensureAllSections(initialSections))
  const lastSectionsJsonRef = useRef<string>("")
  const lastContentHtmlRef = useRef<string>("")

  const updateSection = (sectionId: string, newContent: any) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, content: { ...section.content, ...newContent } } : section,
      ),
    )
  }

  // Re-hydrate when the parent supplies fresh initialSections (e.g. switching
  // between campaigns).
  useEffect(() => {
    if (initialSections && initialSections.length > 0) {
      setSections(ensureAllSections(initialSections))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSections])

  // Stitch the per-section serializers into the complete email document. The
  // footer is reused verbatim from the webinar builder (one shared contact
  // footer across the complete-HTML layouts).
  const generateHtmlFromSections = (toRender: BrochureSection[]): string => {
    let html = EMAIL_DOCUMENT_OPEN
    toRender.forEach((section) => {
      switch (section.type) {
        case "hero":
          html += heroToHtml(section, tenantName)
          break
        case "highlights":
          html += highlightsToHtml(section)
          break
        case "showcase":
          html += showcaseToHtml(section)
          break
        case "cta":
          html += ctaToHtml(section)
          break
      }
    })
    html += footerToHtml(
      { id: "footer", type: "footer", content: {} } as any,
      tenantName,
      tenantInfo,
      new Date().getFullYear(),
    )
    html += EMAIL_DOCUMENT_CLOSE
    return html
  }

  // Regenerate HTML whenever the sections change. Guarded by JSON / HTML refs so
  // the parent's callback identity changes can't spin an infinite render loop
  // (same pattern as the webinar builder).
  useEffect(() => {
    const sectionsJson = JSON.stringify(sections)
    if (sectionsJson === lastSectionsJsonRef.current) return
    lastSectionsJsonRef.current = sectionsJson

    const html = generateHtmlFromSections(sections)
    if (html !== lastContentHtmlRef.current) {
      lastContentHtmlRef.current = html
      onContentChange(html)
    }
    if (onSectionsChange) onSectionsChange(sections)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, onContentChange, onSectionsChange])

  const renderSection = (section: BrochureSection) => {
    switch (section.type) {
      case "hero":
        return <HeroSection section={section} onUpdate={updateSection} />
      case "highlights":
        return <HighlightsSection section={section} onUpdate={updateSection} />
      case "showcase":
        return <ShowcaseSection section={section} onUpdate={updateSection} />
      case "cta":
        return <CtaSection section={section} onUpdate={updateSection} />
      default:
        return null
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden", bgcolor: "background.paper", maxWidth: "800px", mx: "auto", width: "100%" }}>
        {sections.map((section) => (
          <div key={section.id}>{renderSection(section)}</div>
        ))}
      </Box>
    </Box>
  )
}
